import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, NavLink, Link, useLocation, Navigate } from "react-router-dom";
import {
    LayoutDashboard,
    ClipboardCheck,
    Thermometer,
    Receipt,
    Wallet,
    FileText,
    BookOpen,
    Settings as SettingsIcon,
    Leaf,
    FolderOpen,
    CalendarDays,
    Menu,
    X,
    LogOut,
    UserCircle2,
    Sparkles,
    CreditCard,
    Store,
    Smartphone,
} from "lucide-react";
import { Toaster } from "sonner";

import Dashboard from "./pages/Dashboard";
import Haccp from "./pages/Haccp";
import Temperature from "./pages/Temperature";
import Fatture from "./pages/Fatture";
import Spese from "./pages/Spese";
import Documenti from "./pages/Documenti";
import Scadenze from "./pages/Scadenze";
import Report from "./pages/Report";
import Manuale from "./pages/Manuale";
import Impostazioni from "./pages/Impostazioni";
import Onboarding from "./pages/Onboarding";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Billing from "./pages/Billing";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import Admin from "./pages/Admin";
import Landing from "./pages/Landing";
import Privacy from "./pages/Privacy";
import Termini from "./pages/Termini";
import Scarica from "./pages/Scarica";
import ClubLayout from "./pages/club/ClubLayout";
import ClubDashboard from "./pages/club/ClubDashboard";
import ClubMembers from "./pages/club/ClubMembers";
import ClubMemberDetail from "./pages/club/ClubMemberDetail";
import ClubSale from "./pages/club/ClubSale";
import ClubBackup from "./pages/club/ClubBackup";
import ClubNewMember from "./pages/club/ClubNewMember";
import ReminderBanner from "./components/ReminderBanner";
import UpdateBanner from "./components/UpdateBanner";
import LanguageSwitcher from "./components/LanguageSwitcher";
import { clubName } from "./lib/clubApi";
import { startReminderLoop } from "./lib/reminders";
import { registerServiceWorker } from "./lib/push";
import { api } from "./lib/api";
import { AuthProvider, useAuth } from "./lib/auth";
import { initLang } from "./lib/i18n";

initLang();

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/haccp", label: "Autocontrollo", icon: ClipboardCheck, testid: "nav-haccp" },
    { to: "/temperature", label: "Temperature", icon: Thermometer, testid: "nav-temperature" },
    { to: "/scadenze", label: "Scadenze", icon: CalendarDays, testid: "nav-scadenze" },
    { to: "/fatture", label: "Fatture", icon: Receipt, testid: "nav-fatture" },
    { to: "/spese", label: "Contabilità", icon: Wallet, testid: "nav-spese" },
    { to: "/documenti", label: "Documenti", icon: FolderOpen, testid: "nav-documenti" },
    { to: "/report", label: "Report PDF", icon: FileText, testid: "nav-report" },
    { to: "/manuale", label: "Manuale HACCP", icon: BookOpen, testid: "nav-manuale" },
    { to: "/billing", label: "Abbonamento", icon: CreditCard, testid: "nav-billing" },
    { to: "/impostazioni", label: "Impostazioni", icon: SettingsIcon, testid: "nav-impostazioni" },
];

function TrialBanner({ user }) {
    if (!user || user.subscription_status !== "trialing" || !user.trial_ends_at) return null;
    const end = new Date(user.trial_ends_at);
    const days = Math.max(0, Math.ceil((end - Date.now()) / 86400000));
    return (
        <Link
            to="/billing"
            className="block bg-accent-warm/15 text-terracotta border-b border-accent-warm/30 px-4 py-2 text-sm hover:bg-accent-warm/25"
            data-testid="trial-banner"
        >
            <div className="flex items-center justify-center gap-2">
                <Sparkles size={14} />
                <span className="font-medium">
                    Prova gratuita: {days} {days === 1 ? "giorno rimasto" : "giorni rimasti"}
                </span>
                <span className="hidden sm:inline opacity-80">· Passa a 9,99€/mese →</span>
            </div>
        </Link>
    );
}

function UserCard({ user, onLogout }) {
    if (!user) return null;
    const initials = (user.name || user.email || "U").slice(0, 2).toUpperCase();
    return (
        <div className="mt-6 pt-4 border-t border-soft" data-testid="sidebar-user-card">
            <div className="flex items-center gap-3 px-2 mb-3">
                <div className="w-9 h-9 rounded-full bg-forest-50 text-forest grid place-items-center font-semibold text-sm">
                    {initials}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-forest-dark truncate" data-testid="sidebar-user-name">{user.name || "Utente"}</div>
                    <div className="text-xs text-stone-500 truncate" data-testid="sidebar-user-email">{user.email}</div>
                    {user.role === "admin" && (
                        <div className="text-[10px] font-bold uppercase tracking-wider text-accent-warm">Admin</div>
                    )}
                </div>
            </div>
            <button
                onClick={onLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-stone-600 hover:bg-bone-2"
                data-testid="sidebar-logout"
            >
                <LogOut size={16} /> Esci
            </button>
        </div>
    );
}

function SidebarContent({ shopName, shopSubtitle, shop, user, onNavigate, onLogout }) {
    return (
        <>
            <Link to="/" onClick={onNavigate} className="flex items-center gap-2 mb-8 px-2" data-testid="logo-link">
                <div className="w-10 h-10 rounded-xl bg-forest text-white grid place-items-center">
                    <Leaf size={20} />
                </div>
                <div className="min-w-0">
                    <div className="font-display text-lg leading-tight text-forest-dark truncate">{shopName}</div>
                    <div className="text-xs text-stone-500 -mt-0.5 truncate">{shopSubtitle}</div>
                </div>
            </Link>
            <nav className="flex flex-col gap-1">
                {NAV.map(({ to, label, icon: Icon, end, testid }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end={end}
                        onClick={onNavigate}
                        data-testid={testid}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                isActive
                                    ? "bg-forest text-white shadow-[0_4px_12px_rgba(44,76,59,0.18)]"
                                    : "text-stone-600 hover:bg-bone-2"
                            }`
                        }
                    >
                        <Icon size={18} />
                        <span>{label}</span>
                    </NavLink>
                ))}
                {user?.role === "admin" && (
                    <NavLink
                        to="/admin"
                        onClick={onNavigate}
                        data-testid="nav-admin"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                isActive
                                    ? "bg-accent-warm text-white shadow-[0_4px_12px_rgba(229,126,82,0.25)]"
                                    : "text-accent-warm hover:bg-accent-warm/10"
                            }`
                        }
                    >
                        <UserCircle2 size={18} />
                        <span>Admin</span>
                    </NavLink>
                )}
                <NavLink
                    to="/club"
                    onClick={onNavigate}
                    data-testid="nav-samir-club"
                    className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                            isActive
                                ? "bg-terracotta text-white shadow-[0_4px_12px_rgba(200,90,60,0.25)]"
                                : "text-terracotta hover:bg-terracotta/10"
                        }`
                    }
                >
                    <Store size={18} />
                    <span className="truncate">{clubName(shop)}</span>
                </NavLink>
                <Link
                    to="/scarica"
                    onClick={onNavigate}
                    data-testid="nav-scarica-app"
                    className="mt-3 flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold bg-forest-800 text-white shadow-[0_4px_12px_rgba(44,76,59,0.25)] hover:opacity-90 transition-opacity"
                >
                    <Smartphone size={18} />
                    <span>Scarica l&apos;app</span>
                </Link>
            </nav>
            <div className="mb-3 flex items-center justify-between px-1" data-testid="sidebar-language">
                <span className="text-[11px] font-bold uppercase tracking-widest text-stone-400">Lingua</span>
                <LanguageSwitcher />
            </div>
            <UserCard user={user} onLogout={onLogout} />
            <div className="mt-4 text-xs text-stone-400 px-2">
                <p>Reg. CE 852/2004</p>
                <p className="mt-1">v1.0 · Made with care</p>
            </div>
        </>
    );
}

function DesktopSidebar(props) {
    return (
        <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white border-r border-soft min-h-screen px-4 py-6 sticky top-0" data-testid="desktop-sidebar">
            <SidebarContent {...props} />
        </aside>
    );
}

function MobileDrawer({ open, onClose, ...props }) {
    return (
        <>
            <div
                onClick={onClose}
                className={`lg:hidden fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                data-testid="mobile-drawer-backdrop"
            />
            <aside
                className={`lg:hidden fixed top-0 left-0 z-50 w-72 max-w-[85vw] h-full bg-white border-r border-soft px-4 py-6 flex flex-col overflow-y-auto transition-transform duration-250 ${open ? "translate-x-0" : "-translate-x-full"}`}
                data-testid="mobile-drawer"
            >
                <button onClick={onClose} aria-label="Chiudi menu" className="self-end p-2 -mt-2 -mr-2 mb-2 text-stone-500 hover:text-stone-800" data-testid="mobile-drawer-close">
                    <X size={22} />
                </button>
                <SidebarContent {...props} onNavigate={onClose} />
            </aside>
        </>
    );
}

function TopBar({ onMenuClick }) {
    const loc = useLocation();
    const current = NAV.find((n) => (n.end ? n.to === loc.pathname : loc.pathname.startsWith(n.to))) || NAV[0];
    const today = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    return (
        <header className="bg-bone sticky top-0 z-30 px-4 sm:px-6 lg:px-8 pt-5 pb-3">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <button onClick={onMenuClick} aria-label="Apri menu" className="lg:hidden p-2 -ml-2 rounded-lg text-forest-dark hover:bg-bone-2" data-testid="topbar-menu-btn">
                        <Menu size={22} />
                    </button>
                    <div className="min-w-0">
                        <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-forest-500 truncate">{today}</p>
                        <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold text-forest-dark mt-0.5 truncate">{current.label}</h1>
                    </div>
                </div>
                <Link to="/fatture" className="btn-accent inline-flex items-center gap-2 text-sm shrink-0" data-testid="topbar-upload-cta">
                    <Receipt size={16} />
                    <span className="hidden sm:inline">Carica fattura</span>
                </Link>
            </div>
        </header>
    );
}

function Layout({ children, shop, user, onLogout }) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const loc = useLocation();
    useEffect(() => setDrawerOpen(false), [loc.pathname]);
    useEffect(() => {
        document.body.style.overflow = drawerOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [drawerOpen]);
    const shopName = shop?.ragione_sociale || "HACCP Manager";
    const shopSubtitle = shop?.tipo_attivita || "Autocontrollo";
    return (
        <div className="flex min-h-screen bg-bone">
            <DesktopSidebar shopName={shopName} shopSubtitle={shopSubtitle} shop={shop} user={user} onLogout={onLogout} />
            <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} shopName={shopName} shopSubtitle={shopSubtitle} shop={shop} user={user} onLogout={onLogout} />
            <main className="flex-1 min-w-0 pb-8">
                <TrialBanner user={user} />
                <TopBar onMenuClick={() => setDrawerOpen(true)} />
                <div className="px-4 sm:px-6 lg:px-8 pb-8">{children}</div>
            </main>
        </div>
    );
}

function ProtectedShell() {
    const { user, logout } = useAuth();
    const loc = useLocation();
    const [shop, setShop] = useState(null);
    const [shopChecked, setShopChecked] = useState(false);

    // If subscription is canceled or expired, force redirect to /billing
    // (admin bypasses this since they always have access)
    const isInactive = user?.role !== "admin" && (user?.subscription_status === "canceled" || user?.subscription_status === "expired" || user?.active === false);

    const refreshShop = async () => {
        try {
            const r = await api.get("/settings");
            setShop(r.data);
        } catch {
            setShop({});
        } finally {
            setShopChecked(true);
        }
    };

    useEffect(() => {
        document.title = "HACCP Manager · Autocontrollo";
        startReminderLoop();
        registerServiceWorker();
        if (!isInactive) {
            refreshShop();
        } else {
            setShopChecked(true);
            setShop({ onboarded: true }); // skip onboarding for inactive users
        }
    }, [isInactive]);

    if (!shopChecked) {
        return <div className="min-h-screen bg-bone grid place-items-center text-stone-500 text-sm">Caricamento...</div>;
    }

    // Inactive subscription → force /billing page, no other access
    if (isInactive && loc.pathname !== "/billing") {
        return <Navigate to="/billing" replace />;
    }

    if (!isInactive && !shop?.onboarded) {
        return <Onboarding onComplete={refreshShop} />;
    }

    return (
        <Layout shop={shop} user={user} onLogout={logout}>
            <ReminderBanner />
            <Routes>
                <Route path="/" element={<Dashboard user={user} shop={shop} />} />
                <Route path="/haccp" element={<Haccp />} />
                <Route path="/temperature" element={<Temperature />} />
                <Route path="/scadenze" element={<Scadenze />} />
                <Route path="/fatture" element={<Fatture />} />
                <Route path="/spese" element={<Spese />} />
                <Route path="/documenti" element={<Documenti />} />
                <Route path="/report" element={<Report />} />
                <Route path="/manuale" element={<Manuale />} />
                <Route path="/impostazioni" element={<Impostazioni onShopUpdate={refreshShop} />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Layout>
    );
}

function AppRoutes() {
    const { user } = useAuth();
    const loc = useLocation();

    // Public legal pages — accessible without login
    if (loc.pathname === "/privacy") return <Privacy />;
    if (loc.pathname === "/termini") return <Termini />;
    if (loc.pathname === "/scarica") return <Scarica />;

    // Loading state
    if (user === undefined) {
        return <div className="min-h-screen bg-bone grid place-items-center text-stone-500 text-sm">Caricamento...</div>;
    }

    // Not logged in → only show public routes
    if (!user) {
        return (
            <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="*" element={<Navigate to="/login" state={{ from: loc }} replace />} />
            </Routes>
        );
    }

    // Logged-in but billing pages bypass the shop/onboarding gate
    if (loc.pathname === "/billing/success") return <BillingSuccess />;
    if (loc.pathname === "/billing/cancel") return <BillingCancel />;

    // Club soci — separate mobile-first layout, available to every tenant
    if (loc.pathname.startsWith("/club")) {
        return (
            <Routes>
                <Route path="/club" element={<ClubLayout />}>
                    <Route index element={<ClubDashboard />} />
                    <Route path="nuovo" element={<ClubNewMember />} />
                    <Route path="clienti" element={<ClubMembers />} />
                    <Route path="clienti/:id" element={<ClubMemberDetail />} />
                    <Route path="vendita" element={<ClubSale />} />
                    <Route path="backup" element={<ClubBackup />} />
                </Route>
                <Route path="/club/*" element={<Navigate to="/club" replace />} />
            </Routes>
        );
    }

    return <ProtectedShell />;
}

function App() {
    return (
        <div className="App">
            <Toaster position="top-right" richColors />
            <UpdateBanner />
            <BrowserRouter>
                <AuthProvider>
                    <AppRoutes />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
